// Suggesting which received deposit belongs to which declared remittance. PURE (no network): the
// database decides what is ALLOWED (matched totals can never exceed either side); this only
// decides what to SHOW first. Money is integer ngwee; dates are 'YYYY-MM-DD' strings compared as
// text and converted with day arithmetic (never `new Date('YYYY-MM-DD')`).

export interface OpenRemittance {
  id: string
  departmentId: string
  declaredNgwee: number
  matchedNgwee: number
  movedOn: string
  /** Normalised bank reference ('FT 26-03' and 'ft2603' are the same), or null. */
  refNorm: string | null
}

export interface OpenReceipt {
  id: string
  /** Department it is attributed to, or null while nobody has claimed it. */
  departmentId: string | null
  receivedNgwee: number
  allocatedNgwee: number
  receivedOn: string
  refNorm: string | null
}

export interface Suggestion<R extends OpenReceipt = OpenReceipt> {
  receipt: R
  score: number
  /** Plain-language reasons, shown next to the suggestion. */
  reasons: string[]
  /** The most that can be matched right now: the smaller of what is left on each side. */
  maxMatchNgwee: number
}

const DAY_MS = 86_400_000
function dayNumber(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) return NaN
  return Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS)
}

/** Whole days from `a` to `b` (positive when b is later). */
export function daysBetween(a: string, b: string): number {
  return dayNumber(b) - dayNumber(a)
}

export const remainingOnRemittance = (r: OpenRemittance) => r.declaredNgwee - r.matchedNgwee
export const remainingOnReceipt = (r: OpenReceipt) => r.receivedNgwee - r.allocatedNgwee

/**
 * Rank the receipts that could be matched to a remittance, best first. A receipt is excluded
 * when it is used up, or already attributed to a different department.
 */
export function suggestMatches<R extends OpenReceipt>(remittance: OpenRemittance, receipts: R[]): Suggestion<R>[] {
  const left = remainingOnRemittance(remittance)
  const out: Suggestion<R>[] = []
  for (const r of receipts) {
    const rleft = remainingOnReceipt(r)
    if (rleft <= 0 || left <= 0) continue
    if (r.departmentId !== null && r.departmentId !== remittance.departmentId) continue
    let score = 0
    const reasons: string[] = []
    if (remittance.refNorm && r.refNorm && remittance.refNorm === r.refNorm) {
      score += 100
      reasons.push('Same bank reference')
    }
    if (rleft === left) {
      score += 50
      reasons.push('Same amount')
    } else if (r.receivedNgwee === remittance.declaredNgwee) {
      score += 30
      reasons.push('Same amount as declared')
    }
    const gap = daysBetween(remittance.movedOn, r.receivedOn)
    // Money normally arrives on or after the day it was sent, within a couple of weeks.
    if (gap >= -1 && gap <= 14) {
      score += Math.max(1, 20 - gap)
      reasons.push(gap <= 0 ? 'Received the same day' : `Received ${gap} ${gap === 1 ? 'day' : 'days'} later`)
    }
    if (r.departmentId === remittance.departmentId) {
      score += 10
      reasons.push('Already attributed to this department')
    }
    out.push({ receipt: r, score, reasons, maxMatchNgwee: Math.min(left, rleft) })
  }
  // Best first; on a tie the older receipt, so the order does not jump around between refreshes.
  return out.sort((a, b) => b.score - a.score || a.receipt.receivedOn.localeCompare(b.receipt.receivedOn) || a.receipt.id.localeCompare(b.receipt.id))
}
